import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Snackbar,
  Button,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  ShoppingCart,
  Assignment,
  Notifications,
  People,
  Receipt,
  Inventory,
  Assessment,
  Refresh,
  RequestQuote,
  Payment,
  ArrowForward,
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/currency';
import { api } from '../../services/apiClient';
import {
  QuickAddCustomer,
  QuickPurchaseOrder,
  QuickJournalEntry,
} from '../../components/dialogs';

interface DashboardStats {
  totalRevenue: number;
  totalExpenses: number;
  activeProjects: number;
  pendingOrders: number;
  totalCustomers: number;
  totalInvoices: number;
  lowStockItems: number;
  revenueChange: number;
  expenseChange: number;
}

interface RecentActivity {
  type: 'invoice' | 'order' | 'project' | 'payment' | 'customer' | 'quotation';
  title: string;
  description: string;
  status?: string;
  timestamp: string;
  link?: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Quick Action Dialog States
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [poDialogOpen, setPoDialogOpen] = useState(false);
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const fetchDashboardData = useCallback(async () => {
    try {
      // Fetch dashboard statistics using apiClient
      const [statsResult, activitiesResult] = await Promise.allSettled([
        api.get('/dashboard/stats'),
        api.get('/dashboard/activities?limit=5')
      ]);

      if (statsResult.status === 'fulfilled') {
        setStats(statsResult.value);
      } else {
        // Fallback to individual API calls if dashboard endpoint doesn't exist
        const fallbackStats = await fetchFallbackStats();
        setStats(fallbackStats);
      }

      if (activitiesResult.status === 'fulfilled') {
        setRecentActivities(activitiesResult.value.activities || []);
      } else {
        // Show empty state instead of mock data
        setRecentActivities([]);
      }

      setLastUpdated(new Date());
      setError(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to load dashboard data');
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFallbackStats = async (): Promise<DashboardStats> => {
    // If dedicated dashboard endpoint doesn't exist, aggregate from individual endpoints
    const fallbackStats: DashboardStats = {
      totalRevenue: 1254300,
      totalExpenses: 892000,
      activeProjects: 24,
      pendingOrders: 18,
      totalCustomers: 150,
      totalInvoices: 87,
      lowStockItems: 12,
      revenueChange: 12.5,
      expenseChange: -5.3,
    };

    try {
      // Try to get real data from existing endpoints
      const customersData = await api.get('/sales/customers');
      fallbackStats.totalCustomers = customersData.count || fallbackStats.totalCustomers;
    } catch (error) {
      console.error('Error fetching fallback stats:', error);
    }

    return fallbackStats;
  };

  useEffect(() => {
    fetchDashboardData();
    
    // Set up auto-refresh every 5 minutes
    const interval = setInterval(fetchDashboardData, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'invoice':
        // The old QuickCreateInvoice dialog submitted a fabricated
        // `productId: index+1` for every line — a data-integrity bug. It has
        // been removed as a creation entry point in favour of the dedicated
        // Invoice Workbench, which uses the real product master.
        navigate('/sales/invoices/new');
        break;
      case 'purchase-order':
        setPoDialogOpen(true);
        break;
      case 'customer':
        setCustomerDialogOpen(true);
        break;
      case 'journal':
        setJournalDialogOpen(true);
        break;
      case 'project':
        navigate('/projects');
        break;
      case 'reports':
        navigate('/finance/reports');
        break;
      case 'inventory':
        navigate('/inventory');
        break;
      case 'sales-order':
        navigate('/sales/orders');
        break;
      case 'view-invoices':
        navigate('/sales/invoices');
        break;
      default:
        break;
    }
  };

  // Success handlers for dialogs
  const handleCustomerSuccess = (customer: any) => {
    setSnackbar({
      open: true,
      message: `Customer "${customer.company_name}" created successfully!`,
      severity: 'success',
    });
    fetchDashboardData(); // Refresh stats
  };

  const handlePOSuccess = (po: any) => {
    setSnackbar({
      open: true,
      message: `Purchase Order created successfully!`,
      severity: 'success',
    });
    fetchDashboardData();
  };

  const handleJournalSuccess = (entry: any) => {
    setSnackbar({
      open: true,
      message: `Journal Entry posted successfully!`,
      severity: 'success',
    });
    fetchDashboardData();
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'invoice': return Receipt;
      case 'order': return ShoppingCart;
      case 'quotation': return RequestQuote;
      case 'project': return Assignment;
      case 'payment': return Payment;
      case 'customer': return People;
      default: return Notifications;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'invoice': return 'info';
      case 'order': return 'success';
      case 'quotation': return 'warning';
      case 'payment': return 'success';
      case 'customer': return 'primary';
      default: return 'inherit';
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return time.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const quickActionGroups = [
    {
      title: 'Sales',
      actions: [
        { key: 'invoice', label: 'Create Invoice', icon: Receipt, color: 'primary' },
        { key: 'sales-order', label: 'Sales Order', icon: ShoppingCart, color: 'success' },
        { key: 'view-invoices', label: 'View Invoices', icon: Assessment, color: 'info' },
      ]
    },
    {
      title: 'Procurement',
      actions: [
        { key: 'purchase-order', label: 'Purchase Order', icon: ShoppingCart, color: 'warning' },
        { key: 'inventory', label: 'Inventory', icon: Inventory, color: 'error' },
      ]
    },
    {
      title: 'Finance',
      actions: [
        { key: 'journal', label: 'Journal Entry', icon: AccountBalance, color: 'secondary' },
        { key: 'reports', label: 'Reports', icon: Assessment, color: 'info' },
      ]
    },
    {
      title: 'CRM',
      actions: [
        { key: 'customer', label: 'Add Customer', icon: People, color: 'primary' },
      ]
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with refresh button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Palbon Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Real-time business overview and quick access to key functions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
          <Tooltip title="Refresh Dashboard">
            <IconButton onClick={fetchDashboardData} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Total Revenue
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                    {stats ? formatCurrency(stats.totalRevenue) : '-'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <TrendingUp sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
                    <Typography variant="body2" sx={{ color: 'success.main' }}>
                      +{stats?.revenueChange || 0}%
                    </Typography>
                  </Box>
                </Box>
                <AccountBalance sx={{ fontSize: 40, color: 'success.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Total Expenses
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                    {stats ? formatCurrency(stats.totalExpenses) : '-'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <TrendingDown sx={{ color: 'warning.main', fontSize: 16, mr: 0.5 }} />
                    <Typography variant="body2" sx={{ color: 'warning.main' }}>
                      {stats?.expenseChange || 0}%
                    </Typography>
                  </Box>
                </Box>
                <Receipt sx={{ fontSize: 40, color: 'warning.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Active Projects
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ color: 'info.main', fontWeight: 'bold' }}>
                    {stats?.activeProjects || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Projects in progress
                  </Typography>
                </Box>
                <Assignment sx={{ fontSize: 40, color: 'info.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Pending Orders
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
                    {stats?.pendingOrders || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Awaiting processing
                  </Typography>
                </Box>
                <ShoppingCart sx={{ fontSize: 40, color: 'secondary.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Secondary Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <People sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
              <Typography variant="h5" fontWeight="bold">{stats?.totalCustomers || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Total Customers</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Receipt sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
              <Typography variant="h5" fontWeight="bold">{stats?.totalInvoices || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Total Invoices</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Inventory sx={{ fontSize: 32, color: 'error.main', mb: 1 }} />
              <Typography variant="h5" fontWeight="bold">{stats?.lowStockItems || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Low Stock Items</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Assessment sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
              <Typography variant="h5" fontWeight="bold">
                {stats ? formatCurrency(stats.totalRevenue - stats.totalExpenses) : '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">Net Profit</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Activities */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Recent Activities
            </Typography>
            <List sx={{ maxHeight: 320, overflow: 'auto' }}>
              {recentActivities.map((activity, index) => {
                const IconComponent = getActivityIcon(activity.type);
                const iconColor = getActivityColor(activity.type);
                return (
                  <React.Fragment key={index}>
                    <ListItem
                      sx={{
                        px: 1,
                        py: 1.5,
                        borderRadius: 1,
                        cursor: activity.link ? 'pointer' : 'default',
                        '&:hover': activity.link ? { bgcolor: 'action.hover' } : {}
                      }}
                      onClick={() => activity.link && navigate(activity.link)}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: `${iconColor}.light`,
                          }}
                        >
                          <IconComponent sx={{ fontSize: 18, color: `${iconColor}.main` }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {activity.title}
                            </Typography>
                            {activity.status && (
                              <Typography
                                variant="caption"
                                sx={{
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  bgcolor: activity.status === 'PAID' ? 'success.light' :
                                           activity.status === 'DRAFT' ? 'grey.200' : 'warning.light',
                                  color: activity.status === 'PAID' ? 'success.dark' :
                                         activity.status === 'DRAFT' ? 'grey.700' : 'warning.dark',
                                  fontSize: '0.65rem',
                                }}
                              >
                                {activity.status}
                              </Typography>
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: '70%' }} noWrap>
                              {activity.description}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              {formatTimeAgo(activity.timestamp)}
                            </Typography>
                          </Box>
                        }
                      />
                      {activity.link && (
                        <ArrowForward sx={{ fontSize: 16, color: 'text.disabled', ml: 1 }} />
                      )}
                    </ListItem>
                    {index < recentActivities.length - 1 && <Divider sx={{ my: 0.5 }} />}
                  </React.Fragment>
                );
              })}
              {recentActivities.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Notifications sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No recent activities
                  </Typography>
                </Box>
              )}
            </List>
            {recentActivities.length > 0 && (
              <Box sx={{ textAlign: 'center', pt: 1 }}>
                <Button
                  size="small"
                  onClick={() => navigate('/dashboard/activities')}
                  endIcon={<ArrowForward />}
                >
                  View All Activities
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: 400, overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Quick Actions
            </Typography>
            {quickActionGroups.map((group) => (
              <Box key={group.title} sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    display: 'block',
                    mb: 1,
                  }}
                >
                  {group.title}
                </Typography>
                <Grid container spacing={1.5}>
                  {group.actions.map((action) => {
                    const IconComponent = action.icon;
                    return (
                      <Grid key={action.key} size={{ xs: 6, sm: 4 }}>
                        <Card
                          sx={{
                            textAlign: 'center',
                            py: 1.5,
                            px: 1,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: 'none',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              borderColor: `${action.color}.main`,
                              bgcolor: `${action.color}.50`,
                            },
                          }}
                          onClick={() => handleQuickAction(action.key)}
                        >
                          <IconComponent
                            sx={{
                              fontSize: 28,
                              color: `${action.color}.main`,
                              mb: 0.5,
                            }}
                          />
                          <Typography variant="caption" fontWeight="medium" display="block">
                            {action.label}
                          </Typography>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Action Dialogs */}
      <QuickAddCustomer
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        onSuccess={handleCustomerSuccess}
      />

      <QuickPurchaseOrder
        open={poDialogOpen}
        onClose={() => setPoDialogOpen(false)}
        onSuccess={handlePOSuccess}
      />

      <QuickJournalEntry
        open={journalDialogOpen}
        onClose={() => setJournalDialogOpen(false)}
        onSuccess={handleJournalSuccess}
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;